import {IProduct} from 'modules/product-card/models/ProductResponse';
import {REVIEWS_CODE} from 'modules/product-card/constants';
import {
    getProductBrand,
    getProductCanonSection,
    getProductSpecialCategoryData,
    isProductFineAndRare,
} from 'modules/product-card/helpers';
import {getAnalyticsPrice} from 'modules/google-analytics/helpers/price';
import {EventAction, EventCurrencyCodes, ProductAvailabilityType} from 'modules/google-analytics/constants';
import {ISearchData} from 'modules/search-popup/models';
import {buildProductStoreEventContext} from 'modules/analytics/helpers/buildProductStoreEventContext';
import {getStoreEventContext} from 'modules/analytics/helpers/getStoreEventContext';

export const getProductRating = (ratings: IProduct['ratings'] = []) => {
    return ratings?.find(({code}) => REVIEWS_CODE === code);
};

export const getProductYear = (product: IProduct) => {
    return Array.isArray(product.properties) ? product.properties?.find(({code}) => 'year' === code) : undefined;
};

export const getProductAvailability = (product: IProduct) => {
    const {canBuy, canReserve} = product;

    if (canBuy || canReserve) {
        return ProductAvailabilityType.Available;
    }

    return ProductAvailabilityType.NotAvailable;
};

export const getProductPageViewMainData = (product: IProduct, eventLocation: string) => {
    const {brand} = product;
    const {id, price: priceData, title} = product;

    const {discountValue, price} = priceData.discount;

    const category = getProductSpecialCategoryData(product);
    const rating = getProductRating(product.ratings);
    const year = getProductYear(product);
    const availability = getProductAvailability(product);

    return {
        categoryName: category?.title ?? '',
        categoryId: category?.id ?? '',
        productAvailability: availability,
        productBrand: brand?.title ?? '',
        productId: id,
        productName: title,
        productRateLevel: rating?.value ?? '',
        productVariant: year?.value ?? '',
        pageType: eventLocation,
        eventContext: [buildProductStoreEventContext(getStoreEventContext(product))],
        ecommerce: {
            currencyCode: EventCurrencyCodes.RUB,
            detail: {
                actionField: {
                    list: eventLocation,
                },
                products: [
                    {
                        category: category?.title ?? '',
                        dimension3: discountValue,
                        dimension13: rating?.value ? String(rating.value) : '0',
                        id,
                        list: eventLocation,
                        metric1: getAnalyticsPrice(product.price.base.price),
                        name: title,
                        price: getAnalyticsPrice(price),
                        quantity: 1,
                        variant: year?.value ?? '',
                    },
                ],
            },
        },
    };
};

export const getAddToCartEventLabel = (product: Partial<IProduct>, eventAction = EventAction.Add) => {
    const {price, name, title} = product;
    const isAddAction = eventAction === EventAction.Add;

    return `${isAddAction ? 'cart' : 'product'} | ${isAddAction ? name || title : EventAction.Remove} | ${isAddAction ? price?.discount.discountValue || 0 : name || title}`;
};

export const getProductCartMainData = (
    product: Partial<IProduct & ISearchData['products'][number]>,
    eventPosition = 1,
    eventAction = EventAction.Add,
) => {
    const {id, price, name, title, sectionId, sectionTitle} = product;

    const eventLabel = getAddToCartEventLabel(product, eventAction);
    const category = getProductCanonSection(product.sections ?? []);
    const rating = getProductRating(product.ratings);

    return {
        eventCategoryId: category?.id ?? sectionId ?? 'null',
        eventCategoryName: category?.title || sectionTitle || 'null',
        eventLabel,
        eventPosition,
        eventProductId: id ? String(id) : 'null',
        eventProductName: name || title || 'null',
        eventProductPrice: getAnalyticsPrice(price?.base.price),
        eventProductRateLevel: rating?.value ?? 'null',
        // eventProductRateNumber: reviewsData?.assessmentsCount ?? 'null',
    };
};

// eslint-disable-next-line complexity
export const getProductEcommerceData = (product: Partial<IProduct & ISearchData['products'][number]>, quantity = 1) => {
    const category = getProductCanonSection(product.sections ?? []);
    const rating = getProductRating(product.ratings);

    const {article, title, name, meta, canBuy, canReserve, price, manufacturer, year, saleCodes, sectionTitle} =
        product;

    const salesArray = meta?.saleCodes ?? saleCodes ?? [];
    const dimension14 = 0 < salesArray.length ? salesArray.join('|') : '(not set)';

    return {
        id: article,
        name: name ?? title,
        brand: getProductBrand(product as any) || manufacturer || 'null',
        category: category?.title || sectionTitle || 'null',
        variant: String(getProductYear(product as any)?.value || year || null),
        price: getAnalyticsPrice(price?.specPrice || price?.discount?.price || price?.base?.price),
        dimension3: price?.discount?.discountValue || 'null',
        dimension14,
        // в зависимости от доступности товара 1- товар доступен для покупки, 0 - не доступен
        dimension25: canBuy || canReserve ? 1 : 0,
        // рейтинг продукта в звездах (по отзывам пользователей). Передается только в карточке товара, в остальных местах = null
        dimension13: rating?.value ? String(rating.value) : '0',
        dimension48: isProductFineAndRare(product) ? '1' : '0',
        metric1: getAnalyticsPrice(price?.base.price),
        quantity,
        metric2: quantity,
    };
};
